import type { DocumentNode } from "graphql";
import { getMainDefinition } from "@apollo/client/utilities";

type ApolloOperationReportInput = {
  operationName?: unknown;
  variables?: unknown;
};

type ApolloErrorConsoleSummaryInput = {
  graphQLErrorCount: number;
  hasNetworkError: boolean;
  operationName?: unknown;
};

/** Backend refusal when a member edits the email of the contact they sign in as. */
export const OWN_LOGIN_EMAIL_REFUSAL = "This email is your Member Dashboard login.";

/**
 * Backend refusal of the old credential tools for a Chamber Member Pro member
 * whose logins belong to its contacts. It is an expected answer, not a fault.
 */
export const MEMBER_CONTACT_LOGINS_MESSAGE =
  "This member's Member Dashboard logins belong to its contacts. Use Update Member > Member Contacts in Chamber Member Pro to send credentials or reset a password.";

export const isMemberContactLoginsRefusal = (message: unknown): boolean =>
  typeof message === "string" &&
  message.startsWith("This member's Member Dashboard logins belong to its contacts.");

/**
 * The API refuses a request whose sign-in token expired or is no longer
 * valid ("Context creation failed: You are not authorized to make this
 * request", Sentry CPA-ADMIN-CONSOLE-69 and -10, Oct 2026). The page renews
 * the session and retries once, or sends the member to sign in.
 */
export const isSessionAuthRefusal = (message: unknown): boolean =>
  typeof message === "string" &&
  message.includes("You are not authorized to make this request");

/** Amplify's rejection when there is no signed-in user (Sentry CPA-ADMIN-CONSOLE-K). */
export const isNoSignedInUserError = (error: unknown): boolean =>
  error === "No current user" || error === "The user is not authenticated";

const locallyHandledApolloErrorOperations = new Set([
  "reactivateMemberProfileAndPerksBannersForChamberMember",
  "reactivateBannerForCategoryAdvertisingBanner",
  "getChamberForMemberLogin",
  "getCmpMemberDashboardChamberBySlug",
  "recordCmpMemberDashboardLogin",
  "recordCpaDashboardAccess",
  "sendLoginInfoEmailToChamberMember",
  "ActivateCmpMember",
  "SuspendCmpMember",
  "getCmpMemberInvoices",
  "getCmpMemberInvoiceDetail",
  "getCmpMemberInvoiceDocument",
  "startCmpMemberInvoiceCheckout",
]);

const getOperationName = (operationName: unknown): string =>
  typeof operationName === "string" && operationName.trim()
    ? operationName.trim()
    : "unknown";

/**
 * Error reports can identify the GraphQL input shape, but must not include
 * customer or credential values from the operation variables.
 */
export const getApolloOperationReportContext = ({
  operationName,
  variables,
}: ApolloOperationReportInput): {
  operationName: string;
  variableNames: string[];
} => ({
  operationName: getOperationName(operationName),
  variableNames:
    variables && typeof variables === "object" && !Array.isArray(variables)
      ? Object.keys(variables as Record<string, unknown>).sort()
      : [],
});

export const getApolloErrorConsoleSummary = ({
  graphQLErrorCount,
  hasNetworkError,
  operationName,
}: ApolloErrorConsoleSummaryInput): {
  graphQLErrorCount: number;
  hasNetworkError: boolean;
  operationName: string;
} => ({
  graphQLErrorCount,
  hasNetworkError,
  operationName: getOperationName(operationName),
});

// A screen that passes `{ preserveErrorForCaller: true }` as the operation
// context shows the error itself (member Events, Auto Renewal, Contact Cards).
export const isApolloErrorHandledLocally = (
  operationName: unknown,
  operationContext?: { preserveErrorForCaller?: unknown }
): boolean =>
  operationContext?.preserveErrorForCaller === true ||
  (typeof operationName === "string" &&
    locallyHandledApolloErrorOperations.has(operationName));

type PageLifecycleTarget = Pick<Window, "addEventListener">;

/**
 * Tracks whether the page is unloading (reload, close or full navigation). Only
 * pagehide sets the flag: beforeunload can be cancelled by the unsaved-changes
 * prompt. A page restored from the back/forward cache fires pageshow and clears it.
 * Same rule as the staff dashboard (chamber-member-pro apolloErrorPolicy).
 */
export const createPageUnloadTracker = (target: PageLifecycleTarget) => {
  let pageIsUnloading = false;
  target.addEventListener("pagehide", () => { pageIsUnloading = true; });
  target.addEventListener("pageshow", () => { pageIsUnloading = false; });
  return { isPageUnloading: () => pageIsUnloading };
};

/**
 * The browser cancels in-flight requests when the page unloads; they reject with
 * "Failed to fetch" / "Load failed" or an AbortError and no HTTP status. Those are
 * not API failures, so they are not reported. Errors with a status still are.
 */
export const isNetworkErrorFromPageUnload = ({ networkError, pageIsUnloading }: {
  networkError?: { name?: string; statusCode?: unknown } | null;
  pageIsUnloading: boolean;
}): boolean => {
  if (!networkError || typeof networkError.statusCode === "number") return false;
  return pageIsUnloading || networkError.name === "AbortError";
};

/** True for a mutation (an action the user started), false for a query or subscription. */
export const isMutationOperation = (query: DocumentNode | undefined): boolean => {
  if (!query) return false;
  try {
    const definition = getMainDefinition(query);
    return definition.kind === "OperationDefinition" && definition.operation === "mutation";
  } catch {
    return false;
  }
};

/** An ApolloError (or the same shape from another Apollo copy). */
export const isApolloErrorLike = (error: unknown): boolean =>
  Boolean(
    error &&
      typeof error === "object" &&
      Array.isArray((error as { graphQLErrors?: unknown }).graphQLErrors) &&
      "networkError" in (error as object)
  );
